import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Building2, Search, Briefcase, Mail, Phone, Clock } from "lucide-react";
import { Link } from "react-router-dom";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/users", { params: { role: "client" } }).then((r) => setClients(r.data.filter((u) => u.role === "client")));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const merged = useMemo(() => {
    const out = [];
    const coveredNames = new Set();
    for (const c of clients) {
      const projs = projects.filter((p) => p.client_id === c.id || (p.client_name || "").trim().toLowerCase() === (c.name || "").trim().toLowerCase());
      projs.forEach((p) => p.client_name && coveredNames.add(p.client_name.trim().toLowerCase()));
      out.push({ key: c.id, portal: true, name: c.name, email: c.email, phone: c.phone, status: c.status, last_login_at: c.last_login_at, projs });
    }
    const byName = {};
    for (const p of projects) {
      const nm = (p.client_name || "").trim();
      if (!nm || coveredNames.has(nm.toLowerCase())) continue;
      (byName[nm.toLowerCase()] = byName[nm.toLowerCase()] || { key: `rec-${nm.toLowerCase()}`, portal: false, name: nm, projs: [] }).projs.push(p);
    }
    return [...out, ...Object.values(byName)];
  }, [clients, projects]);

  const filtered = merged.filter((c) =>
    !q || c.name?.toLowerCase().includes(q.toLowerCase()) || c.email?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5 animate-fade-up" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: "#72BE44" }}>Relationships</p>
          <h1 className="text-3xl font-bold tracking-tight text-stone-900 flex items-center gap-3">
            <Building2 className="w-7 h-7" style={{ color: "#145353" }} /> Client Management
          </h1>
          <p className="text-sm text-stone-500 mt-1">{merged.length} clients · {clients.length} with portal access</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients..." data-testid="clients-search"
            className="pl-9 pr-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-[#72BE44] w-64" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => {
          const projs = c.projs;
          const active = projs.filter((p) => p.status === "Active").length;
          return (
            <div key={c.key} className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-[0_8px_24px_rgba(20,83,83,0.08)] transition" data-testid={`client-card-${c.key}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold shrink-0" style={{ background: c.portal ? "#145353" : "#58595B" }}>
                  {(c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-stone-900 truncate">{c.name}</div>
                  {c.portal
                    ? <span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded-full ${(c.status || "Active") === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{c.status || "Active"} · Portal</span>
                    : <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">No portal account</span>}
                </div>
                <div className="ml-auto text-right shrink-0">
                  <div className="text-2xl font-bold" style={{ color: "#72BE44" }}>{projs.length}</div>
                  <div className="text-[9px] uppercase tracking-wider text-stone-400 font-bold">Projects</div>
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-stone-600">
                {c.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 shrink-0" style={{ color: "#72BE44" }} /> {c.email}</div>}
                {c.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" style={{ color: "#72BE44" }} /> {c.phone}</div>}
                {c.portal && <div className="flex items-center gap-2 text-stone-400"><Clock className="w-3.5 h-3.5 shrink-0" /> Last login: {c.last_login_at ? new Date(c.last_login_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Never"}</div>}
              </div>
              {projs.length > 0 && (
                <div className="mt-4 pt-3 border-t border-stone-100">
                  <div className="text-[9px] uppercase tracking-wider text-stone-400 font-bold mb-1.5">{active} active project(s)</div>
                  <div className="space-y-1">
                    {projs.slice(0, 3).map((p) => (
                      <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center gap-2 text-xs text-stone-700 hover:text-[#145353] hover:underline truncate">
                        <Briefcase className="w-3 h-3 shrink-0" style={{ color: "#145353" }} /> {p.project_name} <span className="text-stone-400">· {p.service_type}</span>
                      </Link>
                    ))}
                    {projs.length > 3 && <div className="text-[10px] text-stone-400">+{projs.length - 3} more…</div>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-stone-400">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            No clients found{q ? ` for "${q}"` : " — client accounts are created automatically on project conversion"}
          </div>
        )}
      </div>
    </div>
  );
}
